import axios from 'axios';
import { getCurrentLanguage } from './i18n';
import i18n from './i18n';
import { getAccessToken } from './utils/accessToken';
import { ensureAuthSession } from './utils/ensureAuthSession';
import { clearKnownAuthSession, hasKnownAuthSession } from './utils/authSessionState';
import store from './Reducers/store';
import { CommercialControlActionTypes, UserActionTypes } from './Actions/actionTypes';

export const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api/";

export const api = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    timeout: 15000,
});

function isAuthEndpoint(url = "") {
    return url.includes("users/login") || url.includes("users/refresh") || url.includes("users/logout");
}

function isPolicyAcceptanceRequired(status, data) {
    const backendMessage = data?.error || data?.message || data?.detail;
    return status === 403 && String(backendMessage || "").trim() === "POLICY_ACCEPTANCE_REQUIRED";
}

async function syncPolicyAcceptanceState() {
    const response = await api.get("users/is-auth", {
        skipAuthRefresh: true,
        skipPolicyAcceptanceSync: true,
    });
    const user = response?.data?.data?.user;
    if (user) {
        store.dispatch({
            type: UserActionTypes.SET_POLICY_ACCEPTANCE,
            payload: user,
        });
    }
}

function redirectToLogin() {
    if (hasKnownAuthSession()) {
        sessionStorage.setItem("logout_message", i18n.t('common:general.session_expired_reconnect'));
        sessionStorage.setItem("logout_message_reason", "session_expired");
    } else {
        sessionStorage.removeItem("logout_message");
        sessionStorage.removeItem("logout_message_reason");
    }
    clearKnownAuthSession();
    store.dispatch({ type: CommercialControlActionTypes.COMMERCIAL_RESET });
    window.location.href = "/";
}

api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        config.headers['X-Client-Language'] = getCurrentLanguage();
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const requestUrl = error.config?.url || "";

        const errMsg =
            data?.error ||
            data?.message ||
            data?.detail ||
            i18n.t('common:general.network_or_server_error');

        if (isPolicyAcceptanceRequired(status, data) && !error.config?.skipPolicyAcceptanceSync) {
            try {
                await syncPolicyAcceptanceState();
            } catch {
                // Keep the original API error; the next auth check will retry the policy sync.
            }
        }

        if (error.config?.skipAuthRefresh) {
            error.response = {
                ...error.response,
                status,
                data: {
                    ...data,
                    message: errMsg
                }
            };
            return Promise.reject(error);
        }

        // 403 = droits insuffisants (on garde la session),
        // 401 = access token expire: refresh cookie HttpOnly, then replay once.
        if (status === 401 && error.config && !error.config._retry && !isAuthEndpoint(requestUrl)) {
            error.config._retry = true;

            try {
                const token = await ensureAuthSession({ forceRefresh: true });
                error.config.headers = error.config.headers || {};
                if (token) {
                    error.config.headers.Authorization = `Bearer ${token}`;
                } else {
                    delete error.config.headers.Authorization;
                }
                return api(error.config);
            } catch (refreshError) {
                redirectToLogin();
                return Promise.reject(refreshError);
            }
        }

        if (status === 401 && !isAuthEndpoint(requestUrl)) {
            redirectToLogin();
        }

        error.response = {
            ...error.response,
            status,
            data: {
                ...data,
                message: errMsg
            }
        };

        return Promise.reject(error);
    }
);
